import { Injectable } from '@angular/core';

export type TProfileMultiSelectPresentation = 'explicit' | 'unfiltered';

export interface IProfileMultiSelectState<TValue> {
  presentation: TProfileMultiSelectPresentation;
  uiSelectedValues: TValue[];
}

@Injectable({
  providedIn: 'root',
})
export class ProfileMultiSelectFilterService {
  public createExplicitSelectionState<TValue>(uiSelectedValues: TValue[]): IProfileMultiSelectState<TValue> {
    return {
      presentation: 'explicit',
      uiSelectedValues,
    };
  }

  public createUnfilteredSelectionState<TValue>(): IProfileMultiSelectState<TValue> {
    return {
      presentation: 'unfiltered',
      uiSelectedValues: [],
    };
  }

  public buildSelectionSummary(firstSelectedLabel: string, selectedCount: number, moreLabel: string): string {
    if (selectedCount <= 1) {
      return firstSelectedLabel;
    }

    return `${firstSelectedLabel}, +${selectedCount - 1} ${moreLabel}`;
  }

  public normalizeSelectedValues<TValue>(availableValues: TValue[], requestedValues: Iterable<TValue>): TValue[] {
    const requestedValueSet = new Set(requestedValues);

    return availableValues.filter((value) => requestedValueSet.has(value));
  }

  public normalizeValuesForEmission<TValue>(
    state: IProfileMultiSelectState<TValue>,
    availableValues: TValue[],
  ): Set<TValue> {
    if (state.uiSelectedValues.length === availableValues.length) {
      return new Set();
    }

    return new Set(state.uiSelectedValues);
  }

  public syncSelectionState<TValue>(
    availableValues: TValue[],
    requestedValues: Set<TValue>,
    currentState: IProfileMultiSelectState<TValue>,
  ): IProfileMultiSelectState<TValue> {
    if (!availableValues.length) {
      return this.createUnfilteredSelectionState<TValue>();
    }

    if (requestedValues.size > 0) {
      return this.createExplicitSelectionState(this.normalizeSelectedValues(availableValues, requestedValues));
    }

    const shouldPreserveExplicitAllSelection =
      currentState.presentation === 'explicit' && currentState.uiSelectedValues.length === availableValues.length;

    return shouldPreserveExplicitAllSelection
      ? this.createExplicitSelectionState(availableValues)
      : this.createUnfilteredSelectionState<TValue>();
  }
}
